// Supplier credit detail: one credit against a source bill, with its lines
// and lifecycle actions. Draft credits can be marked sent or voided; sent
// credits are applied (posts the AP adjustment, flips to Applied).
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link, useParams } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { CardField } from "@/components/RecordCard";
import {
  applySupplierCredit,
  getSupplierCredit,
  type SupplierCreditDetail,
  transitionSupplierCredit,
} from "@/lib/api";
import { useAccountLabels, useBillNumbers } from "@/lib/labels";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "applied":
      return "success";
    case "sent":
      return "info";
    case "void":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

type TransitionAction = "sent" | "void" | "apply";

interface Confirmation {
  title: string;
  description: string;
  confirmLabel: string;
}

// Confirmation copy for the lifecycle actions: apply posts the AP journals
// and is final, void is final, mark-sent only changes status (no email is
// sent). The apply copy names the bill and the amount so the irreversible
// step can't be mistaken for a status tweak.
function confirmationFor(
  action: TransitionAction,
  detail: SupplierCreditDetail,
  billName: string,
): Confirmation {
  switch (action) {
    case "sent":
      return {
        title: "Mark this supplier credit as sent?",
        description:
          "Marks the credit sent so it can then be applied to its bill. It does not email the supplier.",
        confirmLabel: "Mark sent",
      };
    case "void":
      return {
        title: "Void this supplier credit?",
        description:
          "Voiding is final — this credit will never post, and it can no longer be marked sent or applied.",
        confirmLabel: "Void supplier credit",
      };
    case "apply":
      return {
        title: `Apply this credit of ${formatMoney({
          cents: detail.total_minor,
          currency: "USD",
        })} to ${billName}?`,
        description:
          "Applying posts the AP adjustment journals and reduces what the bill owes. Applied is final — a second apply is rejected rather than double-posted.",
        confirmLabel: "Apply to bill",
      };
  }
}

export function SupplierCreditDetailPage() {
  const { supplierCreditId } = useParams({ strict: false }) as {
    supplierCreditId: string;
  };
  const [detail, setDetail] = useState<SupplierCreditDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{
    message: string;
    journalEntryId?: string;
  } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<TransitionAction | null>(null);
  const accountLabels = useAccountLabels();
  const billNumbers = useBillNumbers();

  const reload = useCallback(() => {
    if (!supplierCreditId) return;
    setError(null);
    getSupplierCredit(supplierCreditId)
      .then(setDetail)
      .catch((cause: unknown) => setError(toErrorMessage(cause)));
  }, [supplierCreditId]);

  useEffect(reload, [reload]);

  const run = async (action: "sent" | "void" | "apply", label: string) => {
    if (!supplierCreditId) return;
    setBusy(action);
    setError(null);
    setNotice(null);
    try {
      if (action === "apply") {
        const result = await applySupplierCredit(supplierCreditId);
        setNotice({
          message:
            "Applied — the bill balance is reduced and the adjustment is posted.",
          journalEntryId: result.journal_entry_id,
        });
      } else {
        await transitionSupplierCredit(supplierCreditId, action);
        setNotice({
          message:
            action === "sent"
              ? "Marked sent — the credit can now be applied to its bill."
              : "Voided — this credit will never post.",
        });
      }
      reload();
    } catch (cause) {
      setError(`${label} failed: ${toErrorMessage(cause)}`);
    } finally {
      setBusy(null);
    }
  };

  const confirm = () => {
    if (!confirming) return;
    const action = confirming;
    setConfirming(null);
    void run(
      action,
      action === "apply" ? "Apply" : action === "sent" ? "Mark sent" : "Void",
    );
  };

  const status = detail?.status.toLowerCase() ?? "";
  const billName =
    detail?.related_bill_id && billNumbers.get(detail.related_bill_id)
      ? `bill ${billNumbers.get(detail.related_bill_id)}`
      : "the linked bill";
  const confirmation =
    confirming && detail
      ? confirmationFor(confirming, detail, billName)
      : null;

  return (
    <div className="grid gap-6">
      <nav aria-label="Breadcrumb" className="text-sm">
        <Link
          to="/bills"
          search={(previous) => ({ ...previous, view: "credits" })}
          className="text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:underline"
        >
          Supplier credits
        </Link>
        <span
          aria-hidden="true"
          className="mx-2 text-[var(--muted-foreground)]"
        >
          /
        </span>
        <span aria-current="page" className="font-medium">
          {detail?.supplier_credit_number ?? supplierCreditId}
        </span>
      </nav>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {notice ? (
        <output className="surface block rounded-xl border border-[var(--success)] p-4 text-sm text-[var(--success)]">
          {notice.message}
          {notice.journalEntryId ? (
            <>
              {" "}
              <Link
                to="/journal/$entryId"
                params={{ entryId: notice.journalEntryId }}
                className="underline underline-offset-4"
              >
                View posted entry →
              </Link>
            </>
          ) : null}
        </output>
      ) : null}

      {detail === null && !error ? (
        <p className="text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : detail ? (
        <>
          <PageHeader
            title={detail.supplier_credit_number}
            description={
              <>
                {detail.supplier_name} · issued{" "}
                {new Date(detail.issue_date).toLocaleDateString()} ·{" "}
                <StatusDot
                  tone={statusTone(detail.status)}
                  label={plainStatus(detail.status)}
                />
              </>
            }
            actions={
              <>
                {status === "draft" ? (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setConfirming("sent")}
                      disabled={busy !== null}
                    >
                      {busy === "sent" ? "Marking…" : "Mark sent"}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setConfirming("void")}
                      disabled={busy !== null}
                    >
                      {busy === "void" ? "Voiding…" : "Void"}
                    </Button>
                  </>
                ) : null}
                {status === "sent" ? (
                  <>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => setConfirming("apply")}
                      disabled={busy !== null}
                      title="Post the adjustment and reduce the bill balance (Applied is final — a second apply is rejected)"
                    >
                      {busy === "apply" ? "Applying…" : "Apply to bill"}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setConfirming("void")}
                      disabled={busy !== null}
                    >
                      {busy === "void" ? "Voiding…" : "Void"}
                    </Button>
                  </>
                ) : null}
              </>
            }
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Total
              </p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">
                {formatMoney({ cents: detail.total_minor, currency: "USD" })}
              </p>
            </div>
            <div className="surface rounded-2xl p-5">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">
                Source bill
              </p>
              <p className="mt-1 text-sm">
                {detail.related_bill_id ? (
                  <Link
                    to="/bills/$billId"
                    params={{ billId: detail.related_bill_id }}
                    className="text-xs text-[var(--action)] underline-offset-4 hover:underline"
                    title={detail.related_bill_id}
                  >
                    {billNumbers.get(detail.related_bill_id)
                      ? `View bill ${billNumbers.get(detail.related_bill_id)}`
                      : "View bill"}{" "}
                    →
                  </Link>
                ) : (
                  <span className="text-[var(--muted-foreground)]">
                    None linked
                  </span>
                )}
              </p>
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                Applying is only possible with a linked bill, and the total must
                fit the bill's remaining balance.
              </p>
            </div>
          </div>

          <div className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block">
            <div className="table-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>What</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {detail.lines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3}>
                        This supplier credit has no lines.
                      </TableCell>
                    </TableRow>
                  ) : (
                    detail.lines.map((line) => (
                      <TableRow key={line.id}>
                        <TableCell>{line.description}</TableCell>
                        <TableCell className="text-xs">
                          {accountLabels.has(line.account_id) ? (
                            accountLabels.get(line.account_id)
                          ) : (
                            <span
                              className="text-[var(--muted-foreground)]"
                              title={line.account_id}
                            >
                              Unknown account
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatMoney({
                            cents: line.amount_minor,
                            currency: "USD",
                          })}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          <div className="stack-list grid gap-3 min-[64rem]:hidden">
            {detail.lines.length === 0 ? (
              <p className="text-sm text-[var(--muted-foreground)]">
                This supplier credit has no lines.
              </p>
            ) : (
              detail.lines.map((line) => (
                <article
                  key={line.id}
                  className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                >
                  <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                    <span className="record-title">{line.description}</span>
                    <span className="tabular-nums">
                      {formatMoney({
                        cents: line.amount_minor,
                        currency: "USD",
                      })}
                    </span>
                  </div>
                  <CardField label="Account">
                    {accountLabels.has(line.account_id) ? (
                      accountLabels.get(line.account_id)
                    ) : (
                      <span
                        className="text-[var(--muted-foreground)]"
                        title={line.account_id}
                      >
                        Unknown account
                      </span>
                    )}
                  </CardField>
                </article>
              ))
            )}
          </div>
        </>
      ) : null}

      <Dialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open) setConfirming(null);
        }}
        title={confirmation?.title ?? ""}
        description={confirmation?.description}
      >
        {confirmation ? (
          <div className="actions-row">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setConfirming(null)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={confirm}>
              {confirmation.confirmLabel}
            </Button>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
